import { useEffect, useState } from 'react';
import { useSubscription } from '../hl/react';
import type { Trade } from '../hl/types';
import { formatShares, formatTime, pxToCents } from './format';
import { mergeTrades, TAPE_CAP } from './trades';
import { useKeptHeight } from './useKeptHeight';
import { useT } from '../i18n/useT';

/** HL sends no frame for a coin with no trades, so "none" can only follow a wait. */
const NO_TRADES_AFTER_MS = 1_000;

interface TradesTapeProps {
  /** "#<encoding>" of the selected outcome side. */
  coin: string;
  cap?: number;
}

/**
 * Recent trades for one outcome side, newest first. `side` is the taker side: "B" = buy (green),
 * "A" = sell (red). Replays on (re)subscribe are deduped by tid. A new coin starts empty, in the height of the last tape.
 */
export default function TradesTape({ coin, cap = TAPE_CAP }: TradesTapeProps) {
  const tr = useT();
  const [tape, setTape] = useState<{ coin: string; trades: Trade[] }>({ coin, trades: [] });
  useSubscription({ type: 'trades', coin }, (data) => {
    const mine = data.filter((t) => t.coin === coin);
    setTape((prev) => ({ coin, trades: mergeTrades(prev.coin === coin ? prev.trades : [], mine, cap) }));
  });
  const trades = tape.coin === coin ? tape.trades : [];
  const [waitedFor, setWaitedFor] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setWaitedFor(coin), NO_TRADES_AFTER_MS);
    return () => clearTimeout(timer);
  }, [coin]);
  const kept = useKeptHeight<HTMLElement>(trades.length > 0);

  return (
    // Time/price/size columns stay left to right in Arabic and Hebrew too, like the order book.
    <section {...kept} aria-label={tr('market.trades.heading')} className="flex flex-col" dir="ltr" data-testid="trades-tape">
      <div className="grid grid-cols-3 px-3 pb-1 text-right text-[11px] uppercase tracking-wide text-muted">
        <span className="text-left">{tr('common.time')}</span>
        <span>{tr('common.price')}</span>
        <span>{tr('common.shares')}</span>
      </div>
      {trades.length === 0 ? (
        <p className="my-auto px-3 py-10 text-center text-sm text-muted">{tr(waitedFor === coin ? 'trades.none' : 'common.loading')}</p>
      ) : (
        <ol className="flex flex-col">
          {trades.map((t) => {
            const buy = t.side === 'B';
            return (
              <li
                key={t.tid}
                data-testid="trade-row"
                className="grid grid-cols-3 px-3 py-[3px] text-right text-xs sm:text-[13px]"
              >
                <span className="num text-left text-muted" title={new Date(t.time).toISOString()}>
                  {formatTime(t.time)}
                </span>
                <span
                  className={`num ${buy ? 'text-yes' : 'text-no'}`}
                  title={tr(buy ? 'trades.buy_at' : 'trades.sell_at', { px: t.px })}
                  data-side={buy ? 'buy' : 'sell'}
                >
                  {pxToCents(t.px)}
                </span>
                <span className="num">{formatShares(t.sz)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
